import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
    Wallet, Clock, CheckCircle,
    RefreshCw, History, Filter, X, Building, Send, ShieldCheck, ArrowUpRight, TrendingUp
} from 'lucide-react';
import API_BASE_URL from '../config/api';

const IncomeWallet = () => {
    const [loading, setLoading] = useState(true);
    const [walletData, setWalletData] = useState(null);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState('all');

    // Withdrawal Modal State
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [payoutMethod, setPayoutMethod] = useState('bank');
    const [transactionPassword, setTransactionPassword] = useState('');
    const [upiId, setUpiId] = useState('');
    const [bankInfo, setBankInfo] = useState({
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        branch_name: '',
        account_holder: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);

    const fetchWalletData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/income_wallet.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setWalletData(data.data);
                // Pre-populate bank info and UPI from response
                const bd = data.data.bank_details;
                if (bd) {
                    setBankInfo({
                        bank_name: bd.bank_name || '',
                        account_number: bd.account_number || '',
                        ifsc_code: bd.ifsc_code || '',
                        branch_name: bd.branch_name || '',
                        account_holder: bd.account_holder || ''
                    });
                    setUpiId(bd.upi_id || '');
                }
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch wallet data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchWalletData();
    }, [fetchWalletData]);

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleWithdrawalSubmit = async (e) => {
        e.preventDefault();

        if (parseFloat(withdrawAmount) > walletData?.summary?.total_balance) {
            return;
        }

        if (parseFloat(withdrawAmount) < 500) {
            alert('Minimum withdrawal amount is ₹500');
            return;
        }

        if (payoutMethod === 'upi' && !/^[\w.-]+@[\w.-]+$/.test(upiId)) {
            alert('Please enter a valid UPI ID');
            return;
        }

        try {
            setIsSubmitting(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/submit_income_withdrawal.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    amount: withdrawAmount,
                    payout_method: payoutMethod,
                    transaction_password: transactionPassword,
                    upi_id: upiId,
                    bank_info: bankInfo
                })
            });
            const data = await response.json();
            if (data.success) {
                setSuccessMessage(data.message);
                // Clear form
                setWithdrawAmount('');
                setTransactionPassword('');
                // Refresh data after 3 seconds
                setTimeout(() => {
                    setSuccessMessage(null);
                    setShowWithdrawModal(false);
                    fetchWalletData();
                }, 4000);
            } else {
                alert(data.message || 'Withdrawal failed');
            }
        } catch (err) {
            console.error(err);
            alert('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && !walletData) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={fetchWalletData}
                        className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200"
                    >
                        Retry
                    </button>
                </div>
            </Layout>
        );
    }

    const transactions = walletData?.transactions || [];
    const summary = walletData?.summary || {};

    const filteredTransactions = filterType === 'all'
        ? transactions
        : transactions.filter(t => t.income_type === filterType);

    const isAmountInvalid = parseFloat(withdrawAmount) > summary.total_balance;

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
                                <Wallet className="text-white" size={24} />
                            </div>
                            Income Wallet
                        </h1>
                        <p className="text-gray-500 mt-1 text-sm">Manage your earnings and withdraw to your account</p>
                    </div>
                    <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                        <button
                            onClick={fetchWalletData}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm font-medium"
                        >
                            <RefreshCw size={18} />
                            Refresh
                        </button>
                        <button
                            onClick={() => setShowWithdrawModal(true)}
                            className="flex-[2] sm:flex-none flex items-center justify-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-transform active:scale-95 shadow-md shadow-indigo-100 text-sm font-bold"
                        >
                            <ArrowUpRight size={18} />
                            Withdraw
                        </button>
                    </div>
                </div>

                {/* Wallet Balance Card */}
                <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-indigo-700 rounded-2xl sm:rounded-3xl p-5 sm:p-7 text-white shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 sm:w-64 sm:h-64 bg-white/10 rounded-full -mr-24 -mt-24 sm:-mr-32 sm:-mt-32 backdrop-blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 sm:w-48 sm:h-48 bg-white/5 rounded-full -ml-16 -mb-16 sm:-ml-24 sm:-mb-24 backdrop-blur-2xl"></div>

                    <div className="relative z-10">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="flex items-center gap-4 sm:gap-5">
                                <div className="p-3 sm:p-4 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-md shadow-inner border border-white/20">
                                    <Wallet size={28} className="sm:hidden" />
                                    <Wallet size={36} className="hidden sm:block" />
                                </div>
                                <div>
                                    <p className="text-indigo-100 text-[10px] sm:text-xs font-bold tracking-wider uppercase opacity-80">Withdrawable Balance</p>
                                    <p className="text-2xl sm:text-4xl font-black mt-1">{formatCurrency(summary.total_balance)}</p>
                                </div>
                            </div>

                            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center justify-around md:justify-end gap-3 sm:gap-6">
                                <div className="text-right border-r border-white/20 pr-3 sm:pr-6">
                                    <p className="text-indigo-100 text-[8px] sm:text-[10px] uppercase font-bold tracking-tighter opacity-70">Total Income</p>
                                    <p className="font-bold text-sm sm:text-lg">{formatCurrency(summary.total_earnings)}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-indigo-100 text-[8px] sm:text-[10px] uppercase font-bold tracking-tighter opacity-70">Withdrawn</p>
                                    <p className="font-bold text-sm sm:text-lg text-emerald-300">{formatCurrency(summary.withdrawal_total)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8 sm:mt-12">
                            {[
                                { label: 'Binary Income', value: summary.binary_total },
                                { label: 'Direct Bonus', value: summary.direct_total },
                                { label: 'Shopping Cred', value: summary.shopping_total },
                                { label: 'Other Bonuses', value: (summary.matching_total || 0) + (summary.other_total || 0) }
                            ].map((item, i) => (
                                <div key={i} className="bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/10 transition-all group cursor-default">
                                    <p className="text-indigo-100 text-[8px] sm:text-[10px] font-bold uppercase tracking-widest group-hover:text-white transition-colors truncate">{item.label}</p>
                                    <p className="font-black text-sm sm:text-xl mt-1 tracking-tight truncate">{formatCurrency(item.value)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Processing Alert */}
                {summary.pending_withdrawal > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-4 animate-pulse">
                        <div className="p-2 bg-amber-100 rounded-xl">
                            <Clock className="text-amber-600" size={24} />
                        </div>
                        <div>
                            <p className="font-bold text-amber-900 text-sm">Withdrawal Pending</p>
                            <p className="text-amber-700 text-xs mt-0.5">
                                {formatCurrency(summary.pending_withdrawal)} is under review by the administration.
                            </p>
                        </div>
                    </div>
                )}

                {/* Transaction History Section */}
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-5 py-5 sm:px-6 sm:py-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-indigo-50 rounded-xl">
                                <History className="text-indigo-600" size={22} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-900">Transaction History</h2>
                                <p className="text-[10px] sm:text-xs text-gray-500 font-medium mt-0.5">Review your wallet activities</p>
                            </div>
                        </div>

                        {/* Filter Container */}
                        <div className="flex items-center gap-2 bg-gray-50/80 p-1.5 rounded-xl border border-gray-100 w-full sm:w-auto">
                            <Filter size={16} className="text-gray-400 ml-2" />
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="text-xs bg-transparent border-none rounded-lg px-2 py-1.5 focus:ring-0 focus:outline-none font-bold text-gray-700 w-full sm:min-w-[150px]"
                            >
                                <option value="all">All Records</option>
                                <option value="binary">Binary</option>
                                <option value="direct">Direct</option>
                                <option value="matching">Matching</option>
                                <option value="performance">Performance</option>
                                <option value="withdrawal">Withdrawals</option>
                                <option value="shopping_credit">Shopping</option>
                            </select>
                        </div>
                    </div>

                    <div className="divide-y divide-gray-100">
                        {filteredTransactions.length === 0 ? (
                            <div className="py-20 flex flex-col items-center justify-center text-gray-400">
                                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 border border-gray-100">
                                    <History size={32} className="opacity-30" />
                                </div>
                                <p className="font-bold text-sm">No transactions found</p>
                                <p className="text-xs mt-1">Try changing your filters</p>
                            </div>
                        ) : (
                            filteredTransactions.map((transaction, index) => {
                                const isWithdrawal = transaction.income_type === 'withdrawal';
                                const isCredit = !isWithdrawal || transaction.status === 'rejected';

                                return (
                                    <div key={index} className="px-5 py-5 sm:px-6 sm:py-6 hover:bg-gray-50/50 transition-all group">
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-3 sm:gap-5 min-w-0">
                                                <div className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl shrink-0 ${isWithdrawal ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                                                    {isWithdrawal ? <ArrowUpRight size={20} className="sm:hidden" /> : <TrendingUp size={20} className="sm:hidden" />}
                                                    {isWithdrawal ? <ArrowUpRight size={24} className="hidden sm:block" /> : <TrendingUp size={24} className="hidden sm:block" />}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <p className="text-xs sm:text-base font-black text-gray-900 uppercase tracking-tight truncate">
                                                            {transaction.income_type.replace(/_/g, ' ')}
                                                        </p>
                                                        <span className={`px-2 py-0.5 text-[8px] sm:text-[10px] font-black rounded-lg border leading-none shrink-0 ${transaction.status === 'approved' || transaction.status === 'credited' || transaction.status === 'paid'
                                                            ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                                                            : transaction.status === 'rejected'
                                                                ? 'bg-red-50 border-red-100 text-red-700'
                                                                : 'bg-amber-50 border-amber-100 text-amber-700'
                                                            }`}>
                                                            {transaction.status.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className="mt-1.5 flex items-center gap-2 text-gray-500 overflow-hidden">
                                                        <p className="text-[10px] sm:text-xs font-bold whitespace-nowrap">{formatDate(transaction.created_at).split(',')[0]}</p>
                                                        <div className="w-1 h-1 bg-gray-300 rounded-full shrink-0" />
                                                        <p className="text-[10px] sm:text-xs font-medium italic truncate">
                                                            {transaction.status === 'rejected' && transaction.remarks 
                                                                ? `Reason: ${transaction.remarks}` 
                                                                : (transaction.description || 'System processed payout')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className={`text-sm sm:text-xl font-black ${isCredit ? 'text-emerald-600' : 'text-red-600'}`}>
                                                    {isCredit ? '+' : '-'}₹{parseFloat(transaction.amount).toLocaleString()}
                                                </p>
                                                <p className="text-[8px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">Settled</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* --- WITHDRAWAL MODAL --- */}
                {showWithdrawModal && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
                        <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg scale-in overflow-hidden relative border border-gray-100 my-auto">
                            {/* Modal Header */}
                            <div className="bg-gradient-to-r from-indigo-700 to-blue-600 p-6 sm:p-8 text-white relative">
                                <div className="absolute top-0 right-0 p-8 opacity-10">
                                    <Send size={100} />
                                </div>
                                <div className="flex justify-between items-center relative z-10">
                                    <div>
                                        <h2 className="text-xl sm:text-2xl font-black">Withdraw Funds</h2>
                                        <p className="text-indigo-100 text-xs sm:text-sm mt-1">Securely transfer to your account</p>
                                    </div>
                                    <button onClick={() => setShowWithdrawModal(false)} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                                        <X size={24} />
                                    </button>
                                </div>
                            </div>

                            <form onSubmit={handleWithdrawalSubmit} className="p-6 sm:p-8">
                                {successMessage ? (
                                    <div className="text-center py-8">
                                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <CheckCircle className="text-green-600" size={48} />
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900">Success!</h3>
                                        <p className="text-gray-600 mt-4 font-medium">{successMessage}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-5 sm:space-y-6">
                                        {/* Amount */}
                                        <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Amount to Withdraw</label>
                                            <div className="relative">
                                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-400">₹</span>
                                                <input
                                                    type="number"
                                                    value={withdrawAmount}
                                                    onChange={(e) => setWithdrawAmount(e.target.value)}
                                                    required
                                                    className={`w-full bg-white border-2 rounded-xl py-3.5 pl-10 pr-4 text-2xl font-black focus:outline-none transition-all ${isAmountInvalid ? 'border-red-200 text-red-600 bg-red-50' : 'border-gray-100 focus:border-indigo-500'}`}
                                                    placeholder="0.00"
                                                />
                                            </div>
                                            <div className="mt-3 flex justify-between items-center">
                                                <span className="text-[10px] font-bold text-gray-400">Min: ₹500</span>
                                                <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">Available: {formatCurrency(summary.total_balance)}</span>
                                            </div>
                                        </div>

                                        {/* Method Selection */}
                                        <div className="grid grid-cols-1 gap-3 sm:gap-4">
                                            <button
                                                type="button"
                                                onClick={() => setPayoutMethod('bank')}
                                                className={`flex flex-col items-center p-4 rounded-2xl border-2 transition-all gap-2 ${payoutMethod === 'bank' ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-md shadow-indigo-100' : 'border-gray-50 bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                                            >
                                                <Building size={24} />
                                                <span className="font-bold text-xs">Bank Transfer</span>
                                            </button>
                                        </div>

                                        {/* Dynamic Fields */}
                                        {payoutMethod === 'bank' && (
                                            <div className="grid grid-cols-2 gap-3 animate-fade-in">
                                                <div className="col-span-2">
                                                    <label className="text-[9px] font-black text-gray-400 uppercase ml-1">Account Holder</label>
                                                    <input value={bankInfo.account_holder} readOnly className="w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs font-bold text-gray-600 cursor-not-allowed" />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[9px] font-black text-gray-400 uppercase ml-1">Bank Name</label>
                                                    <input value={bankInfo.bank_name} readOnly className="w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs font-bold text-gray-600 cursor-not-allowed" />
                                                </div>
                                                <div>
                                                    <label className="text-[9px] font-black text-gray-400 uppercase ml-1">Account Number</label>
                                                    <input value={bankInfo.account_number} readOnly className="w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs font-bold text-gray-600" />
                                                </div>
                                                <div>
                                                    <label className="text-[9px] font-black text-gray-400 uppercase ml-1">IFSC Code</label>
                                                    <input value={bankInfo.ifsc_code} readOnly className="w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs font-bold text-gray-600" />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[9px] font-black text-gray-400 uppercase ml-1">Branch Name</label>
                                                    <input value={bankInfo.branch_name} readOnly className="w-full bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-xs font-bold text-gray-600 cursor-not-allowed" />
                                                </div>
                                            </div>
                                        )}

                                        {/* Password */}
                                        <div className="relative">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block">Transaction PIN</label>
                                            <div className="relative group">
                                                <input
                                                    type="password"
                                                    value={transactionPassword}
                                                    onChange={e => setTransactionPassword(e.target.value)}
                                                    required
                                                    className="w-full bg-gray-50 border border-gray-100 rounded-xl py-3 px-4 pl-12 font-bold focus:outline-none focus:border-indigo-500 focus:bg-white transition-all text-sm"
                                                    placeholder="Enter 4-digit Transaction PIN"
                                                />
                                                <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-500 transition-colors" size={20} />
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting || isAmountInvalid || !withdrawAmount || parseFloat(withdrawAmount) < 500}
                                            className="w-full py-4 bg-gradient-to-r from-indigo-700 to-blue-600 text-white rounded-2xl font-black shadow-xl shadow-blue-200 disabled:opacity-40 disabled:shadow-none hover:shadow-indigo-300 transition-all flex items-center justify-center gap-2 group"
                                        >
                                            {isSubmitting ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : <><Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /> Confirm Withdrawal</>}
                                        </button>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                )}

                <style>{`
                    .scale-in { animation: scaleIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
                    @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                    .animate-fade-in { animation: fadeIn 0.4s ease-out; }
                    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                `}</style>
            </div>
        </Layout>
    );
};

export default IncomeWallet;
